/**
 * Auth helpers — Workspace SSO (internal users) + email-link (external portal).
 *
 * The hd parameter restricts the OAuth picker to the Microflex Google Workspace
 * tenant, so only @microflexfilm.com accounts can sign in to the internal CRM.
 */
import {
  GoogleAuthProvider, signInWithPopup, signOut as fbSignOut,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
  onAuthStateChanged, type User,
} from 'firebase/auth';
import { auth } from './firebase';

const HOSTED_DOMAIN = import.meta.env.VITE_AUTH_HOSTED_DOMAIN || 'microflexfilm.com';

export async function signInInternal(): Promise<User> {
  const provider = new GoogleAuthProvider();
  // Restrict to the Workspace tenant
  provider.setCustomParameters({ hd: HOSTED_DOMAIN, prompt: 'select_account' });
  // Scopes for Gmail/Calendar/Drive when the user grants them later
  provider.addScope('email');
  provider.addScope('profile');
  const result = await signInWithPopup(auth, provider);

  // Belt-and-suspenders domain check (the hd param is a hint, not a guarantee)
  const email = result.user.email ?? '';
  if (!email.endsWith(`@${HOSTED_DOMAIN}`)) {
    await fbSignOut(auth);
    throw new Error(`Sign-in restricted to @${HOSTED_DOMAIN} accounts.`);
  }
  return result.user;
}

const PORTAL_ACTION_URL = `${window.location.origin}/portal/finish-signin`;

export async function sendPortalLink(email: string) {
  await sendSignInLinkToEmail(auth, email, {
    url: PORTAL_ACTION_URL,
    handleCodeInApp: true,
  });
  window.localStorage.setItem('mfx-pending-email', email);
}

export async function completePortalSignIn() {
  if (!isSignInWithEmailLink(auth, window.location.href)) return null;
  const email = window.localStorage.getItem('mfx-pending-email');
  if (!email) throw new Error('Missing email for portal sign-in.');
  const result = await signInWithEmailLink(auth, email, window.location.href);
  window.localStorage.removeItem('mfx-pending-email');
  return result.user;
}

export const signOut = () => fbSignOut(auth);
export const onAuth  = (cb: (u: User | null) => void) => onAuthStateChanged(auth, cb);

/** Read the role from the user's custom claims (set by the setUserClaims function). */
export async function getRole(user: User): Promise<string | null> {
  const token = await user.getIdTokenResult();
  return (token.claims.role as string) ?? null;
}
